/**
 * Using a session this page is not allowed to read.
 *
 * The JWT used to sit in `localStorage`, where any script on the page could read it.
 * It now lives in an HttpOnly cookie the browser attaches by itself, so there is
 * nothing here to store and nothing to decode. What the page does still need is:
 *
 *  - `withCredentials`, or the cookie never leaves;
 *  - `X-Portal`, because all three portals share the API host's cookie jar and the
 *    server has to know which of the three cookies is ours;
 *  - `X-CSRF-Token` on every unsafe request. A cookie rides along on anything the
 *    browser sends, so it no longer proves intent on its own.
 *
 * The CSRF token is handed out in the body of any response that establishes or
 * re-issues a session (sign-in, /auth/refresh, a password change, a rename) and by
 * GET /auth/me. It rotates with the session token, so it is picked up from whatever
 * response happens to carry one rather than only at sign-in — a stale one is a 403.
 *
 * It is kept in a module variable and NOWHERE else: putting it in localStorage would
 * hand back the foothold the cookie just took away.
 */
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

/** Which of the three portals this build is. Constant — see the module comment. */
export const PORTAL = 'clinic';

const SAFE_METHODS = ['get', 'head', 'options'];

// The 403 the API returns when the CSRF token no longer matches the session.
const STALE_CSRF_RE = /could not be verified/i;

let csrfToken = null;
let sessionExpiresAt = null;   // epoch ms, or null when unknown
const listeners = new Set();

/** Notified whenever a response re-establishes the session, with the new expiry. */
export function onSession(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function getSessionExpiresAt() {
  return sessionExpiresAt;
}

/** Forget the session locally. The cookie itself is the server's to clear. */
export function clearSession() {
  csrfToken = null;
  sessionExpiresAt = null;
}

/**
 * Take the CSRF token and expiry out of a response body, if it carries them. Bodies
 * without a `csrf_token` (a PATCH /auth/me that did not re-issue the token, and every
 * ordinary API response) leave the current values alone.
 */
function adoptSession(data) {
  if (!data || typeof data !== 'object') return false;
  if (typeof data.csrf_token !== 'string' || !data.csrf_token) return false;
  csrfToken = data.csrf_token;
  const parsed = Date.parse(data.session_expires_at);
  sessionExpiresAt = Number.isNaN(parsed) ? null : parsed;
  listeners.forEach((fn) => fn(sessionExpiresAt));
  return true;
}

axios.defaults.withCredentials = true;

const BROWSER_TIMEZONE = (() => {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || ''; } catch (_) { return ''; }
})();

axios.interceptors.request.use((config) => {
  config.headers = config.headers || {};
  config.headers['X-Portal'] = PORTAL;
  // The server keeps UTC; "today" — the queue, a doctor's break — is the clinic's own
  // day, so it is told which zone this browser is in.
  if (BROWSER_TIMEZONE) config.headers['X-Timezone'] = BROWSER_TIMEZONE;
  const method = String(config.method || 'get').toLowerCase();
  if (csrfToken && !SAFE_METHODS.includes(method)) {
    config.headers['X-CSRF-Token'] = csrfToken;
  }
  return config;
});

axios.interceptors.response.use(
  (res) => {
    adoptSession(res.data);
    return res;
  },
  async (err) => {
    // A rotated session token means a rotated CSRF token, and a request already in
    // flight can carry the old one. Fetch the current one and try once more; `__csrf`
    // makes sure a request is only ever replayed once.
    const config = err?.config;
    const detail = err?.response?.data?.detail;
    if (
      err?.response?.status === 403
      && typeof detail === 'string'
      && STALE_CSRF_RE.test(detail)
      && config
      && !config.__csrfRetried
    ) {
      config.__csrfRetried = true;
      try {
        const me = await axios.get(`${API_URL}/api/v1/auth/me`);
        if (adoptSession(me.data)) {
          return axios(config);
        }
      } catch {
        // Not recoverable — the original 403 is the more useful error.
      }
    }
    return Promise.reject(err);
  }
);

/**
 * Best-effort sign-out on the server: it revokes the token and clears the cookie.
 * Never blocks, swallows every failure, and returns a promise a caller that is about
 * to leave the page can wait for. `fetch` rather than axios so a failure here cannot
 * re-enter the interceptors above; `credentials: 'include'` because without the cookie
 * there would be nothing to revoke. Logout is exempt from the CSRF check, so a stale
 * token still ends the session — the header is sent when we have one all the same.
 */
export function revokeSession() {
  if (sessionExpiresAt && Date.now() >= sessionExpiresAt) return Promise.resolve();
  const controller = typeof AbortController === 'function' ? new AbortController() : null;
  const timer = controller ? setTimeout(() => controller.abort(), 3000) : null;
  const headers = { 'X-Portal': PORTAL };
  if (csrfToken) headers['X-CSRF-Token'] = csrfToken;
  try {
    return fetch(`${API_URL}/api/v1/auth/logout`, {
      method: 'POST',
      credentials: 'include',
      headers,
      signal: controller?.signal,
    })
      .catch(() => {})
      .finally(() => { if (timer) clearTimeout(timer); });
  } catch {
    if (timer) clearTimeout(timer);
    return Promise.resolve();
  }
}
